import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { RouterModule, Router } from '@angular/router';

import { FormBuilder, FormGroup } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { ReplaySubject, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-select';
import { RepairPartService } from '../../../services/repair-part.service';
import { ToastrService } from '../../../services/toastr-service';
import { RepairPartHeader } from '../../../models/repair-part.model';
import { RepairPartFilterForm } from '../../../form-model/repair-part-filter.form.model';
import { RepairPartFilter } from '../../../models/repair-part-filter.model';
import { RepairPartSearchTextForm } from '../../../form-model/repair-part-search-text.form.model';
import { RepairPartListGridColumns } from './repair-part-grid.config';
import { AuthPolicies } from '../../../auth/auth-policies';

@Component({
  selector: 'app-repair-part-grid',
  imports: [RouterModule, NgxSkeletonLoaderModule, LoaderComponent],
  templateUrl: './repair-part-grid.component.html',
  styleUrl: './repair-part-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairPartGridComponent implements OnInit, AfterViewInit, OnDestroy {
  data: RepairPartHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 30 }, (_, i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: RepairPartFilter = {};
  getRepairPartApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairPartFilterForm: FormGroup<RepairPartFilterForm>;
    repairPartSearchTextForm: FormGroup<RepairPartSearchTextForm>;
  }>;
  columns = RepairPartListGridColumns;
  openDeleteConfirmation: boolean = false;
  selectedRow: RepairPartHeader | null = null;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private readonly repairPartService: RepairPartService,
    private readonly router: Router,
    private readonly toastrService: ToastrService,
    private readonly formBuilder: FormBuilder,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit() {
    this.urlNavigate = '/master-data/repair-part';
    this.getRepairPartList();
  }

  ngAfterViewInit(): void {
    this.ensureFlagControlsExist();
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private ensureFlagControlsExist(): void {
    const filterForm = this.filterFormGroup.controls.repairPartFilterForm;
    const searchForm = this.filterFormGroup.controls.repairPartSearchTextForm;
    if (!filterForm.get('applyRepairPartFilter')) {
      filterForm.addControl('applyRepairPartFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!searchForm.get('appliedRepairPartSearchTextFilter')) {
      searchForm.addControl('appliedRepairPartSearchTextFilter', this.formBuilder.control<boolean | null>(null));
    }
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.controls.repairPartFilterForm
      .get('applyRepairPartFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((applyVal) => {
        if (applyVal === true) {
          this.onApplyFilter();
        }
      });
    this.filterFormGroup.controls.repairPartSearchTextForm
      .get('appliedRepairPartSearchTextFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((searchApplied) => {
        if (searchApplied === true) {
          this.onApplyFilter();
        }
      });
  }

  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = customEvent.detail.column;
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getRepairPartList();
    }
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getRepairPartList();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getRepairPartList(true);
    }
  }

  onEdit(row: RepairPartHeader) {
    const base = this.urlNavigate ?? '/master-data/repair-part';
    this.router.navigate([base, row.repairMode, row.repairCode, row.partCode, 'edit']);
  }

  onDelete(row: RepairPartHeader) {
    this.selectedRow = row;
    this.openDeleteConfirmation = true;
  }

  cancelDelete(): void {
    this.openDeleteConfirmation = false;
    this.selectedRow = null;
  }

  confirmDelete(): void {
    if (!this.selectedRow) {
      this.openDeleteConfirmation = false;
      return;
    }
    const row = this.selectedRow;
    this.repairPartService
      .deleteRepairPart(row.repairMode, row.repairCode, row.partCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.openDeleteConfirmation = false;
          this.selectedRow = null;
          this.getRepairPartList();
          this.toastrService.showToastr(`Repair Code ${row.repairCode} with Part ${row.partCode} deleted successfully`, 'success');
        },
        error: () => {
          this.openDeleteConfirmation = false;
          this.selectedRow = null;
          this.toastrService.showToastr(`Failed to delete association`, 'error');
        },
      });
  }

  onApplyFilter() {
    if (this.filterFormGroup.controls.repairPartFilterForm.valid) {
      const filterForm = this.filterFormGroup.controls.repairPartFilterForm;
      const searchForm = this.filterFormGroup.controls.repairPartSearchTextForm;
      this.filterParam = this.buildFilterParams(filterForm, searchForm);
      this.currentPage = 1;
      this.getRepairPartList();
    }
  }

  private buildFilterParams(filterForm: FormGroup, searchForm: FormGroup): RepairPartFilter {
    const filters: RepairPartFilter = {};
    const modeVal = filterForm.get('repairModeList')?.value;
    if (modeVal && Array.isArray(modeVal) && modeVal.length > 0) {
      filters.repairModeList = modeVal;
    }
    const codeVal = filterForm.get('repairCodeList')?.value;
    if (Array.isArray(codeVal) && codeVal.length > 0) {
      filters.repairCodeList = codeVal;
    }
    const partVal = filterForm.get('partCodeList')?.value;
    if (Array.isArray(partVal) && partVal.length > 0) {
      filters.partCodeList = partVal;
    }
    const opVal = filterForm.get('maxQuantityOperator')?.value;
    if (opVal) {
      filters.maxQuantityOperator = opVal;
    }
    const qtyVal = filterForm.get('maxQuantityValue')?.value;
    if (qtyVal !== undefined && qtyVal !== null && qtyVal !== '') {
      filters.maxQuantityValue = Number(qtyVal);
    }
    const searchVal = searchForm.get('SearchText')?.value;
    if (searchVal) {
      filters.SearchText = searchVal;
    }
    return filters;
  }

  getRepairPartList(isPageChange: boolean = false): void {
    this.data = [];
    this.getRepairPartApiCallStatus = 'IN_PROGRESS';
    this.repairPartService
      .getRepairPartList(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body.map((item) => ({
            ...item,
            id: `${item.repairMode}__${item.repairCode}__${item.partCode}`,
          }));
          this.firstPage = headers?.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers?.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = headers?.get('Current-Page') ? +headers.get('Current-Page')! : 1;
          this.nextPage = headers?.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers?.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers?.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.getRepairPartApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: () => {
          this.getRepairPartApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }

  clearFilter() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairPartSearchTextForm.controls.appliedRepairPartSearchTextFilter.setValue(true);
    this.filterFormGroup.controls.repairPartFilterForm.controls.applyRepairPartFilter.setValue(true);
  }
}
